/**
 * The lot money maths, mirrored from uc-core so the console can show the effect of a correction
 * BEFORE it is saved (F4.10-h). The server stays the authority — it recomputes on save and the
 * console shows the server's answer afterwards. Sources:
 *   uc-core src/modules/gate/utils/deduction-engine.util.ts (applyDeductionRules, validateDeductionRules)
 *   uc-core src/modules/gate/utils/lot-figures.util.ts       (lotFigures)
 *   uc-core src/modules/payout/services/payout.service.ts    (onLotCorrected → payoutAction)
 * All arithmetic is BigInt on grams / paise / basis points; one half-up rounding.
 */
import { divRoundHalfUp } from '@/lib/money'
import { decimalToMinor } from '../api/mappers'

export interface DeductionRule {
  kind: 'MOISTURE'
  from_pct: number
  to_pct: number
  action: 'ACCEPT' | 'DEDUCT' | 'DIVERT'
  base_pct?: number
  deduct_pct_per_point?: number
}

export interface DeductionResult {
  deductionBp: bigint
  payableGrams: bigint
  action: 'ACCEPT' | 'DEDUCT' | 'DIVERT'
  ruleIndex: number | null
}

const hundredths = (v: string | number): bigint => BigInt(decimalToMinor(v, 2) ?? 0)

/** f(card rules, moisture %, net grams) → deduction (bp) and payable grams. */
export function applyDeductionRules(
  rules: ReadonlyArray<DeductionRule> | null | undefined,
  moisturePct: string | number | null | undefined,
  netGrams: bigint,
): DeductionResult {
  const none: DeductionResult = {
    deductionBp: 0n,
    payableGrams: netGrams,
    action: 'ACCEPT',
    ruleIndex: null,
  }
  if (!rules || rules.length === 0) return none
  if (moisturePct === null || moisturePct === undefined || moisturePct === '') return none
  const m = hundredths(moisturePct)
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!
    if (rule.kind !== 'MOISTURE') continue
    if (m < hundredths(rule.from_pct) || m > hundredths(rule.to_pct)) continue
    if (rule.action !== 'DEDUCT') return { ...none, action: rule.action, ruleIndex: i }
    let bp = ((m - hundredths(rule.base_pct ?? 0)) * hundredths(rule.deduct_pct_per_point ?? 0)) / 100n
    if (bp < 0n) bp = 0n
    if (bp > 10000n) bp = 10000n
    const deduction = divRoundHalfUp(netGrams * bp, 10000n)
    return { deductionBp: bp, payableGrams: netGrams - deduction, action: 'DEDUCT', ruleIndex: i }
  }
  return none
}

/** Shape check of a card's deduction rules — the same problems the server answers 422 with. */
export function validateDeductionRules(rules: unknown): string[] {
  if (!Array.isArray(rules)) return ['deductionRules must be an array']
  const problems: string[] = []
  rules.forEach((raw: unknown, i) => {
    const r = (raw ?? {}) as Record<string, unknown>
    const n = i + 1
    if (r.kind === 'TARE') {
      problems.push(`Rule ${n}: TARE is not allowed — sack weight comes from sack types`)
      return
    }
    if (r.kind !== 'MOISTURE') problems.push(`Rule ${n}: kind must be MOISTURE`)
    for (const key of ['from_pct', 'to_pct'] as const) {
      if (typeof r[key] !== 'number' || (r[key] as number) < 0 || Number.isNaN(r[key]))
        problems.push(`Rule ${n}: ${key === 'from_pct' ? 'from %' : 'to %'} must be a number ≥ 0`)
    }
    if (typeof r.from_pct === 'number' && typeof r.to_pct === 'number' && r.to_pct < r.from_pct)
      problems.push(`Rule ${n}: to % must be ≥ from %`)
    if (!['ACCEPT', 'DEDUCT', 'DIVERT'].includes(r.action as string))
      problems.push(`Rule ${n}: action must be ACCEPT, DEDUCT or DIVERT`)
    if (r.action === 'DEDUCT') {
      if (typeof r.base_pct !== 'number' || Number.isNaN(r.base_pct))
        problems.push(`Rule ${n}: base % is required for DEDUCT`)
      if (typeof r.deduct_pct_per_point !== 'number' || Number.isNaN(r.deduct_pct_per_point))
        problems.push(`Rule ${n}: deduct % per point is required for DEDUCT`)
    }
  })
  // Overlapping bands make the first matching rule win silently — flag it for the editor.
  const bands = rules
    .map((r, i) => ({ r: r as Record<string, unknown>, i }))
    .filter(({ r }) => typeof r.from_pct === 'number' && typeof r.to_pct === 'number')
  for (let a = 0; a < bands.length; a++)
    for (let b = a + 1; b < bands.length; b++) {
      const x = bands[a]!
      const y = bands[b]!
      if ((x.r.from_pct as number) <= (y.r.to_pct as number) && (y.r.from_pct as number) <= (x.r.to_pct as number))
        problems.push(`Rules ${x.i + 1} and ${y.i + 1} overlap — the first one wins`)
    }
  return problems
}

export interface BagWeights {
  id: string
  status: string
  grossG: number
  tareG: number
}

export interface LotFigures {
  bagCount: number
  grossG: number
  tareG: number
  netG: number
  deductionBp: number
  payableG: number
  amountPaise: number
}

/** Lot figures from its non-void bags at a ₹/kg rate (paise per kg). */
export function lotFigures(
  bags: ReadonlyArray<{ grossG: number; tareG: number }>,
  ratePaisePerKg: number,
  rules: ReadonlyArray<DeductionRule> | null | undefined,
  moisturePct: string | number | null | undefined,
): LotFigures {
  const gross = bags.reduce((s, b) => s + BigInt(b.grossG), 0n)
  const tare = bags.reduce((s, b) => s + BigInt(b.tareG), 0n)
  const net = gross - tare
  const ded = applyDeductionRules(rules, moisturePct, net)
  const amount = divRoundHalfUp(ded.payableGrams * BigInt(ratePaisePerKg), 1000n)
  return {
    bagCount: bags.length,
    grossG: Number(gross),
    tareG: Number(tare),
    netG: Number(net),
    deductionBp: Number(ded.deductionBp),
    payableG: Number(ded.payableGrams),
    amountPaise: Number(amount),
  }
}

export type PayoutAction = 'NONE' | 'REISSUED' | 'TOP_UP_RAISED' | 'CARRIED_TO_NEXT_LOT' | 'AWAITING_BANK'

export interface PayoutLite {
  kind: string
  status: string
}

const LIVE = new Set(['PENDING_APPROVAL', 'READY', 'SUBMITTING', 'PROCESSING', 'PROCESSED'])
const IN_FLIGHT = new Set(['SUBMITTING', 'PROCESSING'])
const UNSENT = new Set(['PENDING_APPROVAL', 'READY'])

/**
 * What the payout service will do with a correction's money difference (onLotCorrected).
 * `balanceAfterPaise` = Σ the lot's ledger lines + the adjustment. `payouts` in creation order.
 */
export function predictPayoutAction(
  adjustmentPaise: number,
  balanceAfterPaise: number,
  payouts: ReadonlyArray<PayoutLite>,
): PayoutAction {
  if (adjustmentPaise === 0) return 'NONE'
  const lotLive = payouts.find((p) => p.kind === 'LOT' && LIVE.has(p.status))
  const topUpLive = payouts.find((p) => p.kind === 'TOP_UP' && LIVE.has(p.status))
  if ((lotLive && IN_FLIGHT.has(lotLive.status)) || (topUpLive && IN_FLIGHT.has(topUpLive.status)))
    return 'AWAITING_BANK'
  if (lotLive && UNSENT.has(lotLive.status)) {
    if (balanceAfterPaise <= 0) return balanceAfterPaise < 0 ? 'CARRIED_TO_NEXT_LOT' : 'NONE'
    return 'REISSUED'
  }
  if (lotLive?.status !== 'PROCESSED') {
    if (balanceAfterPaise < 0) return 'CARRIED_TO_NEXT_LOT'
    const lastLot = [...payouts].reverse().find((p) => p.kind === 'LOT')
    if (balanceAfterPaise === 0 || (lastLot && (lastLot.status === 'FAILED' || lastLot.status === 'REVERSED')))
      return 'NONE'
    return 'REISSUED'
  }
  if (balanceAfterPaise < 0) return 'CARRIED_TO_NEXT_LOT'
  return balanceAfterPaise > 0 ? 'TOP_UP_RAISED' : 'NONE'
}

export const PAYOUT_ACTION_TEXT: Record<PayoutAction, string> = {
  NONE: 'No payout change.',
  REISSUED: 'The unsent payment is cancelled and raised again for the new amount.',
  TOP_UP_RAISED: 'The lot is already paid: the difference becomes a top-up waiting for approval.',
  CARRIED_TO_NEXT_LOT: 'He was paid too much: the difference is taken from his next lot.',
  AWAITING_BANK: 'A payment is with the bank: the difference is settled when the bank answers.',
}

export interface CorrectionInput {
  bags: ReadonlyArray<BagWeights>
  containerId: string
  /** New gross weight of that bag (grams); null keeps it. */
  newGrossG: number | null
  /** New sack weight (grams) from a new sack type; null keeps it. */
  newTareG: number | null
  ratePaisePerKg: number
  rules: ReadonlyArray<DeductionRule> | null | undefined
  moisturePct: string | number | null | undefined
  /** units.amount_inr as stored (the server diffs against it). */
  currentAmountPaise: number
  /** Σ ledger lines of the lot now (paise). */
  ledgerBalancePaise: number
  payouts: ReadonlyArray<PayoutLite>
}

export interface CorrectionPreview {
  ok: boolean
  errors: string[]
  bagBefore: { grossG: number; tareG: number; netG: number } | null
  bagAfter: { grossG: number; tareG: number; netG: number } | null
  before: LotFigures
  after: LotFigures
  adjustmentPaise: number
  payoutAction: PayoutAction
}

/** The correction's money effect, computed exactly as the server will (F4.10-f/h). */
export function previewCorrection(input: CorrectionInput): CorrectionPreview {
  const live = input.bags.filter((b) => b.status !== 'VOID')
  const before = lotFigures(live, input.ratePaisePerKg, input.rules, input.moisturePct)
  const target = input.bags.find((b) => b.id === input.containerId) ?? null
  const errors: string[] = []
  if (!target) errors.push('Pick a bag of this lot.')
  else if (target.status === 'VOID') errors.push('This bag is void and cannot be corrected.')
  const newGross = input.newGrossG ?? target?.grossG ?? 0
  const newTare = input.newTareG ?? target?.tareG ?? 0
  if (target && newGross === target.grossG && newTare === target.tareG)
    errors.push('Nothing changes: same weight and sack weight.')
  if (target && newGross - newTare < 0) errors.push('The weight is below the sack weight.')
  const afterBags = live.map((b) => (b.id === input.containerId ? { ...b, grossG: newGross, tareG: newTare } : b))
  const after = lotFigures(afterBags, input.ratePaisePerKg, input.rules, input.moisturePct)
  const adjustmentPaise = after.amountPaise - input.currentAmountPaise
  return {
    ok: errors.length === 0,
    errors,
    bagBefore: target ? { grossG: target.grossG, tareG: target.tareG, netG: target.grossG - target.tareG } : null,
    bagAfter: target ? { grossG: newGross, tareG: newTare, netG: newGross - newTare } : null,
    before,
    after,
    adjustmentPaise,
    payoutAction: predictPayoutAction(adjustmentPaise, input.ledgerBalancePaise + adjustmentPaise, input.payouts),
  }
}
